/**
 * section-video-testimonials.js
 *
 * Two jobs:
 *   1. Carousel — arrow scrolling, end-of-track disabling, overflow detection.
 *   2. Click-to-play — no player (iframe or <video>) exists in the DOM until the
 *      visitor asks for one, so a wall of testimonials costs one image each.
 *
 * Everything degrades: with JS off, posters and captions still render and the
 * track is swipeable via native overflow scroll.
 */
(function () {
  'use strict';

  /* ---------- Click-to-play ---------- */

  function stopCard(card) {
    var embed = card.querySelector('[data-tv-embed]');
    var poster = card.querySelector('[data-tv-poster]');
    if (!embed || !poster) return;

    embed.innerHTML = ''; // removes the iframe/video entirely, stopping playback
    embed.hidden = true;
    poster.hidden = false;
    delete card.dataset.tvPlaying;
  }

  function playCard(card, carousel) {
    var template = card.querySelector('[data-tv-template]');
    var embed = card.querySelector('[data-tv-embed]');
    var poster = card.querySelector('[data-tv-poster]');
    if (!template || !embed || !poster) return;

    // Only one video plays at a time within a section.
    var scope = carousel || document;
    scope.querySelectorAll('[data-tv-card][data-tv-playing]').forEach(function (other) {
      if (other !== card) stopCard(other);
    });

    embed.appendChild(template.content.cloneNode(true));
    embed.hidden = false;
    poster.hidden = true;
    card.dataset.tvPlaying = 'true';

    var player = embed.querySelector('video');
    if (player && typeof player.play === 'function') {
      var attempt = player.play();
      if (attempt && typeof attempt.catch === 'function') {
        attempt.catch(function () {
          /* Autoplay blocked — controls are visible, visitor can press play. */
        });
      }
    }

    var focusable = embed.querySelector('video, iframe');
    if (focusable) {
      focusable.setAttribute('tabindex', '-1');
      focusable.focus({ preventScroll: true });
    }
  }

  /* ---------- Carousel ---------- */

  function initCarousel(carousel) {
    if (carousel.dataset.tvReady === 'true') return;
    carousel.dataset.tvReady = 'true';

    var viewport = carousel.querySelector('[data-tv-viewport]');
    var prev = carousel.querySelector('[data-tv-prev]');
    var next = carousel.querySelector('[data-tv-next]');
    var card = carousel.querySelector('[data-tv-card]');
    if (!viewport) return;

    function step() {
      if (!card) return viewport.clientWidth * 0.8;
      var styles = window.getComputedStyle(viewport.querySelector('.tv__track'));
      var gap = parseFloat(styles.columnGap || styles.gap) || 0;
      return card.offsetWidth + gap;
    }

    function update() {
      var overflowing = viewport.scrollWidth - viewport.clientWidth > 2;
      carousel.classList.toggle('tv--overflowing', overflowing);
      if (!prev || !next) return;

      var max = viewport.scrollWidth - viewport.clientWidth;
      prev.disabled = viewport.scrollLeft <= 2;
      next.disabled = viewport.scrollLeft >= max - 2;
    }

    if (prev) {
      prev.addEventListener('click', function () {
        viewport.scrollBy({ left: -step(), behavior: 'smooth' });
      });
    }

    if (next) {
      next.addEventListener('click', function () {
        viewport.scrollBy({ left: step(), behavior: 'smooth' });
      });
    }

    viewport.addEventListener('scroll', function () {
      window.requestAnimationFrame(update);
    });

    carousel.addEventListener('click', function (event) {
      var trigger = event.target.closest('[data-tv-play]');
      if (!trigger) return;
      var target = trigger.closest('[data-tv-card]');
      if (target) playCard(target, carousel);
    });

    if ('ResizeObserver' in window) {
      new ResizeObserver(update).observe(viewport);
    } else {
      window.addEventListener('resize', update);
    }

    update();
  }

  function init(scope) {
    (scope || document).querySelectorAll('[data-tv-carousel]').forEach(initCarousel);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }

  /* ---------- Theme editor ---------- */

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });

  document.addEventListener('shopify:block:select', function (event) {
    var card = event.target.closest('[data-tv-card]');
    var viewport = event.target.closest('[data-tv-viewport]');
    if (!card || !viewport) return;
    viewport.scrollTo({
      left: card.offsetLeft - viewport.offsetLeft - 20,
      behavior: 'smooth'
    });
  });

  document.addEventListener('shopify:section:unload', function (event) {
    event.target.querySelectorAll('[data-tv-card][data-tv-playing]').forEach(stopCard);
  });
})();
